import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOMeta from '@/components/base/SEOMeta';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      setSubmitting(false);
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qta5knlsngrm4lm5ng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = (parsed as Record<string, string>)?.meta?.message
          || (parsed as Record<string, string>)?.message
          || (parsed as Record<string, string>)?.meta?.detail
          || responseText
          || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 换妻一族',
    description: '换妻一族是专注于开放关系与伴侣交换亚文化的深度指南平台，致力于提供客观、理性、全面的资讯与知识。',
    url: 'https://www.example.com/about',
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '换妻一族',
      url: 'https://www.example.com',
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title="关于我们 - 换妻一族"
        description="换妻一族是专注于开放关系与伴侣交换亚文化的深度指南平台，致力于提供客观、理性、全面的资讯与知识。了解我们的品牌理念、用户口碑与联系方式。"
        keywords="换妻一族,关于我们,品牌介绍,联系方式,换妻文化平台"
        canonicalUrl="https://www.example.com/about"
        ogType="website"
      />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow pt-20 pb-12">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />

            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mt-6 mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
              关于我们
            </h1>

            <section className="mb-12">
              <h2 className="text-lg font-semibold text-foreground-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                品牌介绍
              </h2>
              <div className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/70">
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  换妻一族成立于2024年，是国内少数专注于开放关系与伴侣交换亚文化的深度内容平台。我们相信，知识与理性是消除偏见的最佳武器。通过系统化的内容体系——涵盖心理动机、安全守则、法律边界、社群文化、经验分享五大板块——我们希望为所有对此话题感兴趣的人提供一个客观、全面、负责任的资讯来源。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  我们的编辑团队由心理学、法学、社会学等领域的专业人士组成，所有文章均经过多重审核，确保内容的准确性、客观性和可读性。我们严格遵循"不鼓吹、不歧视、不误导"的编辑原则，以中立视角呈现多元观点。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  我们深知这一话题的敏感性，因此在内容呈现上始终秉持负责任的态度：所有文章均在显著位置标注成年人阅读提示，并附有相关法律和安全提醒。我们的目标不是推广某种生活方式，而是为那些已经或正在考虑探索开放关系的人提供可靠的决策参考。
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-lg font-semibold text-foreground-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                    "内容非常专业客观，不像网上那些充满偏见或鼓吹的文章。特别是法律边界的分析，让我对自己的选择有了更清醒的认识。"
                  </p>
                  <p className="text-xs text-foreground-400">—— 上海读者 · 张先生</p>
                </div>
                <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                    "安全守则那几篇文章真的救了我。之前什么都不懂就想去尝试，看完才知道有那么多需要注意的细节。感谢你们的责任心。"
                  </p>
                  <p className="text-xs text-foreground-400">—— 广州读者 · 李女士</p>
                </div>
                <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                    "作为一个心理学专业的学生，我能看出这些文章的扎实程度远超一般的网络文章。引用准确、逻辑清晰，值得推荐。"
                  </p>
                  <p className="text-xs text-foreground-400">—— 北京读者 · 王同学</p>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-lg font-semibold text-foreground-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                联系我们
              </h2>
              <div className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/70">
                <p className="text-sm text-foreground-600 mb-6">
                  如有任何问题、建议或合作意向，欢迎通过以下表单与我们联系。我们将在48小时内回复。
                </p>

                {formStatus === 'success' ? (
                  <div className="bg-accent-100 text-accent-800 rounded-md p-4 text-sm">
                    感谢您的来信！我们将在48小时内回复您。
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form="" className="flex flex-col gap-4">
                    {formStatus === 'error' && formError && (
                      <div className="bg-red-50 text-red-700 rounded-md p-4 text-sm">
                        {formError}
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="name" className="text-sm text-foreground-700">姓名</label>
                        <input
                          id="name"
                          name="name"
                          type="text"
                          required
                          className="px-3 py-2 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="email" className="text-sm text-foreground-700">邮箱</label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          required
                          className="px-3 py-2 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="subject" className="text-sm text-foreground-700">主题</label>
                      <input
                        id="subject"
                        name="subject"
                        type="text"
                        required
                        className="px-3 py-2 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入主题"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="message" className="text-sm text-foreground-700">留言内容</label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        maxLength={500}
                        rows={5}
                        className="px-3 py-2 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      ></textarea>
                      <span className="text-xs text-foreground-400 self-end">最多500字</span>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      className="honeypot-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <button
                      type="submit"
                      disabled={submitting}
                      className="self-start px-6 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-md text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                    >
                      {submitting ? '提交中...' : '提交留言'}
                    </button>
                  </form>
                )}
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}